import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Calculator,
  Play,
  MoreHorizontal,
  Trash2,
  RotateCcw,
  Calendar,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { listSimulators, getSimulator, permanentlyDeleteSimulator, restoreSimulator } from "@/lib/api/simulators";
import { useIsAdmin } from "@/lib/auth/store";
import { formatDate } from "@/lib/utils";
import type { Simulator, SimulatorStatus, SimulatorVersion, SimulatorWithVersions } from "@/types";

export const Route = createFileRoute('/_dashboard/simulators/')({
  component: SimulatorsPage,
})

function SimulatorsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<SimulatorStatus | "all">("all");
  const [page, setPage] = useState(1);
  const [simulatorToDelete, setSimulatorToDelete] = useState<Simulator | null>(null);
  const [versionSelectSimulator, setVersionSelectSimulator] = useState<SimulatorWithVersions | null>(null);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const pageSize = 10;
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["simulators", { page, page_size: pageSize, search, status: status === "all" ? undefined : status }],
    queryFn: () =>
      listSimulators({
        page,
        page_size: pageSize,
        search: search || undefined,
        status: status === "all" ? undefined : status,
      }),
  });

  // Get published versions from simulator
  const getPublishedVersions = (simulator: SimulatorWithVersions): SimulatorVersion[] => {
    return simulator.versions.filter((v) => v.status === "published");
  };

  // Handle simulate button click
  const handleSimulateClick = async (simulator: Simulator) => {
    setIsLoadingVersions(true);
    try {
      const fullSimulator = await getSimulator(simulator.id);
      const publishedVersions = getPublishedVersions(fullSimulator);
      
      if (publishedVersions.length > 1) {
        // Multiple published versions - show selection dialog
        setVersionSelectSimulator(fullSimulator);
      } else if (publishedVersions.length === 1) {
        // Single published version - navigate directly
        navigate({ to: '/simulators/$id/simulate', params: { id: String(simulator.id) } });
      } else {
        // No published versions
        toast.error("Aucune version publiée disponible pour ce simulateur");
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des versions");
    } finally {
      setIsLoadingVersions(false);
    }
  };

  // Handle version selection
  const handleVersionSelect = (versionId: number) => {
    if (versionSelectSimulator) {
      navigate({ 
        to: '/simulators/$id/simulate', 
        params: { id: String(versionSelectSimulator.id) },
        search: { version: versionId }
      });
      setVersionSelectSimulator(null);
    }
  };

  // Format version display text
  const formatVersionDisplay = (version: SimulatorVersion) => {
    const namePart = version.name ? ` - ${version.name}` : "";
    const datePart = version.published_at ? formatDate(version.published_at) : "";
    return {
      title: `Version ${version.version_number}${namePart}`,
      subtitle: datePart ? `Publiée le ${datePart}` : "",
    };
  };

  const deleteMutation = useMutation({
    mutationFn: (simulatorId: number) => permanentlyDeleteSimulator(simulatorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulators"] });
      toast.success("Simulateur supprimé définitivement");
      setSimulatorToDelete(null);
    },
    onError: () => {
      toast.error("Erreur lors de la suppression du simulateur");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (simulatorId: number) => restoreSimulator(simulatorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulators"] });
      toast.success("Simulateur restauré avec succès");
    },
    onError: () => {
      toast.error("Erreur lors de la restauration du simulateur");
    },
  });

  const handleDelete = (simulator: Simulator) => {
    setSimulatorToDelete(simulator);
  };

  const confirmDelete = () => {
    if (simulatorToDelete) {
      deleteMutation.mutate(simulatorToDelete.id);
    }
  };

  const handleRestore = (simulator: Simulator) => {
    restoreMutation.mutate(simulator.id);
  };

  return (
    <div className="flex flex-col h-full">
      <Header title="Simulateurs" />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Actions bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un simulateur..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={status}
              onValueChange={(value) => {
                setStatus(value as SimulatorStatus | "all");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="archived">Archivés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Mis à jour</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-lg" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-48" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-20" /></TableCell>
                    </TableRow>
                  ))
                ) : data?.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Calculator className="h-10 w-10 opacity-50" />
                        <p>Aucun simulateur disponible</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.items.map((simulator, index) => (
                    <TableRow
                      key={simulator.id}
                      className="border-b transition-colors hover:bg-muted/50"
                      style={{
                        animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                      }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Calculator className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <button 
                              onClick={() => handleSimulateClick(simulator)}
                              className="font-medium hover:underline hover:text-primary transition-colors text-left"
                            >
                              {simulator.name}
                            </button>
                            {simulator.description && (
                              <p className="text-sm text-muted-foreground line-clamp-1 max-w-xs">
                                {simulator.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="px-2 py-1 bg-muted rounded text-xs">
                          {simulator.slug}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            simulator.status === "active" ? "success" : "secondary"
                          }
                        >
                          {simulator.status === "active" ? "Actif" : "Archivé"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(simulator.updated_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleSimulateClick(simulator)}
                            disabled={isLoadingVersions}
                          >
                            {isLoadingVersions ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Play className="mr-2 h-4 w-4" />
                            )}
                            Coter
                          </Button>
                          {isAdmin && simulator.status === "archived" && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleRestore(simulator)}>
                                  <RotateCcw className="mr-2 h-4 w-4" />
                                  Restaurer
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDelete(simulator)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Supprimer définitivement
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Delete confirmation dialog */}
        <AlertDialog open={!!simulatorToDelete} onOpenChange={(open) => !open && setSimulatorToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer définitivement ce simulateur ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le simulateur <strong>{simulatorToDelete?.name}</strong> et toutes ses données associées (versions, variables, champs, etc.) seront définitivement supprimés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? "Suppression..." : "Supprimer définitivement"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Version selection dialog */}
        <Dialog open={!!versionSelectSimulator} onOpenChange={(open) => !open && setVersionSelectSimulator(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Choisir une version</DialogTitle>
              <DialogDescription>
                Ce simulateur possède plusieurs versions publiées. Sélectionnez celle que vous souhaitez utiliser.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-4">
              {versionSelectSimulator && getPublishedVersions(versionSelectSimulator)
                .sort((a, b) => b.version_number - a.version_number)
                .map((version) => {
                  const { title, subtitle } = formatVersionDisplay(version);
                  return (
                    <button
                      key={version.id}
                      onClick={() => handleVersionSelect(version.id)}
                      className="w-full flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 hover:border-primary transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center font-bold text-lg text-primary">
                          {version.version_number}
                        </div>
                        <div>
                          <p className="font-medium">{title}</p>
                          {subtitle && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {subtitle}
                            </p>
                          )}
                        </div>
                      </div>
                      <Play className="h-4 w-4 text-muted-foreground" />
                    </button>
                  );
                })}
            </div>
          </DialogContent>
        </Dialog>

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {data.total_items} simulateur{data.total_items > 1 ? "s" : ""} au total
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Précédent
              </Button>
              <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">
                {page} / {data.total_pages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                disabled={page === data.total_pages}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
